import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ConsiderationRow } from '@/api/considerations';
import { roleOptionsQuery } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';

import { vacancyOptionLabel, vacancyTriggerLabel } from './vacancyIdentity';
import { vacancyOptions } from './vacancyOptions';

/**
 * The value that means *no vacancy filter*. Radix refuses `value=""` on an item (it reserves the
 * empty string for "nothing selected"), and a vacancy id is a number, so a word cannot collide with
 * one.
 */
const ALL_VACANCIES = 'all';

interface Props {
  /*
   * ⚠️ **There is no `groups` prop any more** (backend TASK-108). This control took one for a single
   * purpose — deciding whether to read the catalogue, which answers an Interviewer 403 — and every group
   * may call the options route that replaced it. A prop kept "in case a group check comes back" is the
   * kind of thing the next reader treats as a rule; §6.3 still holds `groups` for *Add candidate* and the
   * row menu, which is where that decision belongs.
   */
  /** The viewer, because the options entry is keyed on them (`queryKeys.roleOptions`). */
  viewerId: number;
  /** The rows on screen — the Interviewer's source of options (`vacancyOptions`). */
  rows: ConsiderationRow[];
  selected: number | null;
  onChange: (vacancy: number | null) => void;
}

/**
 * §6.3's vacancy filter — **one control with one source since backend TASK-108.**
 *
 * ⚠️ **It had two.** `GET /api/v1/roles/` answers an **Interviewer 403**, so for them the request was not
 * issued and the options were built from the `role_id`/`role_title` pairs on their own rows — which
 * discloses nothing they cannot see and means a vacancy absent from the current page could not be picked
 * without clearing the filter first. An Admin's filter read the catalogue and had no such limit. The
 * asymmetry was recorded as a **backend** gap by the task that shipped the workaround, and
 * `GET /api/v1/roles/options/` closes it: all three groups may call it, an `Interviewer` is answered the
 * vacancies their own assignments touch, and the payload is what a filter renders and no more — no
 * counts, no `is_open`.
 *
 * ⚠️ **That payload was `{id, title}` until 2026-11-03, and the sentence above said so** (TASK-184/187,
 * ADR 0020 decision 9). The first real ADP import gave this control **96 options** whose titles repeat
 * — *Manual QA Engineer* ×8, *Firmware Engineer* ×7 — so picking one was a choice made blind, and the
 * trigger afterwards read a title two other vacancies also have. `RoleOptionOut` carries
 * `requisition_number` and `hiring_manager` now, and the option reads all three through the module
 * §6.5's consideration block shares (`vacancyIdentity.ts`). ⚠️ **An `Interviewer` receives both keys**,
 * which is the backend's decision and not this control's to narrow: their list is already only the
 * vacancies their own assignments touch, and a payload that varied by group is what ADR 0016 refused by
 * name. Nothing here filters a field out of a payload the server chose to send.
 *
 * `vacancyOptions` still exists and still does the two things only the client can: it keeps a **selected**
 * vacancy in the list even when the source cannot name it, and it falls back to the rows on screen when
 * the request has failed.
 *
 * ⚠️ **A failure of the catalogue is said out loud, never swallowed.** Wrapping the request in a
 * `catch` that hides the control would make a broken deployment look exactly like a group boundary —
 * and `ApiErrorState` exists precisely so that failures have words. The whole screen is not an error
 * state for it: the table is fine and the other two filters work, so the sentence and a Retry sit
 * under this one control, and the filter falls back to the vacancies on this page (`vacancyOptions`)
 * — a narrower list **with** the sentence, rather than a complete-looking one without it.
 *
 * ⚠️ **`vacancy` here, `role_id` on the wire, and the translation is not in this file.** It happens
 * once, in `toConsiderationQuery` (`candidateFilters.ts`) — this component only ever says *vacancy*,
 * which is `backend/CONTEXT.md`'s rule for a product that also has access `Group`s.
 */
export function VacancyFilter({ viewerId, rows, selected, onChange }: Props) {
  const { t } = useTranslation(['candidates', 'common']);

  // ⚠️ **One request for every group, and the entry is keyed on the viewer** — this route *is* narrowed
  // per caller (unlike the catalogue), so `queryKeys.roleOptions` carries the viewer for the reason
  // `queryKeys.roles` deliberately does not: one shared entry would serve an Interviewer's three
  // vacancies to an Admin, or the tenant's twenty to an Interviewer, on a viewer change under a mounted
  // screen. There is no `enabled` gate any more, because there is no group this route refuses.
  const options_ = useQuery(roleOptionsQuery(viewerId));

  const options = vacancyOptions({
    /*
     * ⚠️ **The read gate is gone with the reason for it, and that is worth saying rather than deleting
     * quietly.** It existed because `queryKeys.roles()` carries **no viewer**, so a disabled observer
     * could still resolve from an entry an Admin session had filled and render the whole catalogue to an
     * Interviewer — a leak both `code-reviewer` and `security-auditor` reached independently on
     * 2026-07-28, and one `enabled: false` could not stop, since it suppresses the *request* and not the
     * *read*. The uncovered path was a demotion **without** a re-login (`useSession` has a `staleTime`),
     * which a viewer segment would not have closed either, because a demotion keeps the user id.
     *
     * This control no longer reads that entry at all. It reads a route **every** group may call, keyed on
     * the viewer — so there is no cross-group entry to leak, and the narrowing is the server's.
     */
    catalogue: options_.data,
    rows,
    selected,
    /*
     * ⚠️ **One wording, shared with §6.5 rather than written twice** (BUG-088's lesson: the same
     * thing named two ways in two places is a defect even when both are true). The order of the
     * facts, the words and the separator are `vacancyIdentity.ts`'s, and the **failure** path below
     * deliberately does not reach it — the rows on screen carry a title and nothing else.
     *
     * ⚠️ **Two strings, because the open list and the collapsed trigger have different room**
     * (D-1, 2026-11-04). The list is a portal that sizes to its content, so it reads the title
     * with both facts. The trigger is 224 px wide with ~174 px of text room — **every one** of the
     * 95 real labels is wider, and 27 overflow inside the title alone — so it reads the
     * requisition **first**, where truncation cannot reach it, and the title after. Both come from
     * the same module: this control chooses neither the words nor the order.
     */
    labelForOption: (role) => {
      const vacancy = {
        title: role.title,
        requisitionNumber: role.requisition_number,
        hiringManager: role.hiring_manager,
      };

      return {
        label: vacancyOptionLabel(vacancy, t),
        triggerLabel: vacancyTriggerLabel(vacancy, t),
      };
    },
    labelForUnknown: (id) => t('candidates:filters.vacancyById', { id }),
  });

  return (
    <Field label={t('candidates:filters.vacancy')} className="w-full sm:w-56">
      {(fieldProps) => (
        <>
          <Select
            id={fieldProps.id}
            // The visible `Field` label carries the same words; the `aria-label` is what names the
            // trigger for certain, since a `<label for>` on a Radix trigger depends on the
            // implementation staying a labelable element.
            aria-label={t('candidates:filters.vacancy')}
            value={selected === null ? ALL_VACANCIES : String(selected)}
            onValueChange={(next) => onChange(next === ALL_VACANCIES ? null : Number(next))}
            // With no options and no catalogue there is nothing to choose; the sentence below says
            // why, so this is a control that cannot work rather than one a group may not use (§5).
            disabled={options.length === 0}
            options={[
              { value: ALL_VACANCIES, label: t('candidates:filters.allVacancies') },
              // `triggerLabel` is `undefined` on both fallback paths (a bare title, and the
              // *"Vacancy #77"* of a vacancy neither source can name), and `Select` then renders
              // the label itself — which is what those two are already short enough for.
              ...options.map((option) => ({
                value: String(option.id),
                label: option.label,
                triggerLabel: option.triggerLabel,
              })),
            ]}
          />
          {options_.isError && (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-negative">{t('candidates:filters.vacancyError')}</span>
              <Button variant="ghost" size="sm" onClick={() => void options_.refetch()}>
                {t('common:retry')}
              </Button>
            </span>
          )}
        </>
      )}
    </Field>
  );
}
